import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, putDataAPI } from '../../utils/fetchData';

const TourOverview = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const location = useLocation();
  const [tourPlan, setTourPlan] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const previousPathnameRef = useRef(location.pathname);
  const previousLocationKeyRef = useRef(location.key);
  const isInitialMountRef = useRef(true);

  const loadTourPlan = useCallback(async () => {
    try {
      console.log('📍 Loading tour plan with ID:', planId);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI(`user/tm/tour-plans/${planId}`, token);
      
      console.log('📦 Tour plan response:', response.data);
      
      if (response.data.status === 200) {
        console.log('✅ Tour plan loaded successfully');
        console.log('📋 Shows in tour plan:', response.data.data.tourPlan.shows);
        console.log('📊 Backend progress:', response.data.data.tourPlan.progress);
        setTourPlan(response.data.data.tourPlan);
        setNewName(response.data.data.tourPlan.name);
      } else {
        console.error('❌ Failed to load tour plan:', response.data);
        alert(`Failed to load tour plan: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error loading tour plan:', error);
      alert('Failed to load tour plan. Please check your connection and try again.');
    }
  }, [planId]);

  useEffect(() => {
    loadTourPlan();
    isInitialMountRef.current = false;
  }, [loadTourPlan]);

  // Reload tour plan when navigating back to this page from another route
  useEffect(() => {
    const currentPath = `/tour-manager/tour-overview/${planId}`;
    const previousPath = previousPathnameRef.current;
    const previousKey = previousLocationKeyRef.current;
    const currentKey = location.key;
    const fromShowDetail = location.state?.fromShowDetail;
    const fromFlightSearch = location.state?.fromFlightSearch;
    const fromHotelSearch = location.state?.fromHotelSearch;
    
    // Reload if:
    // 1. We're on this route
    // 2. Location key changed (React Router navigation occurred)
    // 3. It's not the initial mount
    // 4. We came from a different route OR location key changed (handles browser back/forward)
    // 5. OR we came from TourShowDetail, FlightSearch, or HotelSearch (flights/hotels may have been added)
    if (
      location.pathname === currentPath &&
      ((currentKey !== previousKey && !isInitialMountRef.current) || fromShowDetail || fromFlightSearch || fromHotelSearch) &&
      planId
    ) {
      console.log('🔄 Navigated back to TourOverview, reloading tour plan...');
      if (fromShowDetail) {
        console.log('📋 Coming from show detail - refreshing tour progress...');
      }
      if (fromFlightSearch) {
        console.log('✈️ Coming from flight search - refreshing tour progress...');
      }
      if (fromHotelSearch) {
        console.log('🏨 Coming from hotel search - refreshing tour progress...');
      }
      // Small delay to ensure backend has updated
      setTimeout(() => {
        loadTourPlan();
      }, 100);
    }
    
    previousPathnameRef.current = location.pathname;
    previousLocationKeyRef.current = location.key;
  }, [location.pathname, location.key, location.state, planId, loadTourPlan]);

  // Reload tour plan when window gains focus (user navigates back to this tab)
  useEffect(() => {
    const handleFocus = () => {
      if (planId && !isInitialMountRef.current) {
        console.log('🔄 Window focused, reloading tour plan...');
        loadTourPlan();
      }
    };

    // Also reload when page becomes visible again (handles tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && planId && !isInitialMountRef.current) {
        console.log('🔄 Page visible, reloading tour plan...');
        loadTourPlan();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [planId, loadTourPlan]);

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      alert('Tour name cannot be empty');
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI(`user/tm/tour-plans/${planId}/name`, { name: newName }, token);
      
      if (response.data.status === 200) {
        setTourPlan({ ...tourPlan, name: newName });
        setEditingName(false);
      } else {
        alert('Failed to update tour name');
      }
    } catch (error) {
      console.error('Error updating tour name:', error);
      alert('Failed to update tour name');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getProgressColor = (current, total) => {
    const percentage = (current / total) * 100;
    if (percentage === 100) return 'text-green-500';
    if (percentage >= 50) return 'text-yellow-500';
    return 'text-[#FF9500]';
  };

  // Helper to get flight type label for display
  const getFlightTypeLabel = (flight) => {
    const flightType = flight.flightType || flight.flight_type;
    if (flightType === 'return') return 'RETURN';
    if (flightType === 'outbound') return 'DEPARTURE';
    if (flightType === 'connecting') return 'CONNECTING';
    return null;
  };

  // Helper to categorize flights by type for better progress tracking
  const categorizeFlights = (shows) => {
    const flightsByType = {
      departure: [],
      connecting: [],
      return: []
    };
    
    shows?.forEach(show => {
      const showData = typeof show === 'object' ? show : null;
      if (showData?.flights) {
        showData.flights.forEach(flight => {
          const flightType = flight.flightType || flight.flight_type || 'connecting';
          if (flightType === 'outbound' || flightType === 'departure') {
            flightsByType.departure.push(flight);
          } else if (flightType === 'return') {
            flightsByType.return.push(flight);
          } else {
            flightsByType.connecting.push(flight);
          }
        });
      }
    });
    
    return flightsByType;
  };


  if (!tourPlan) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <p className="text-white text-[12px]">Tour plan not found</p>
            <button 
              onClick={() => navigate('/tour-manager/tour-router')}
              className="mt-4 bg-[#FF9500] text-white px-4 py-2 rounded-lg text-[12px]"
            >
              Back to Tour Router
            </button>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  // Calculate actual progress by counting flights/hotels/transport
  // Properly accounts for departure, inter-show, and return flights
  const calculateActualProgress = () => {
    let flightsBooked = 0;
    let hotelsBooked = 0;
    let transportBooked = 0;
    let flightsTotal = 0;
    let hotelsTotal = 0;
    let transportTotal = 0;
    
    console.log('🔢 Calculating progress from tour plan shows:', tourPlan.shows);
    
    // Count flights: count all flights and booked ones separately
    tourPlan.shows?.forEach((show, index) => {
      const showData = typeof show === 'object' ? show : null;
      if (showData?.flights && showData.flights.length > 0) {
        flightsTotal += showData.flights.length;
        const bookedFlights = showData.flights.filter(flight => {
          // Count booked flights of any type (departure, connecting, return)
          return flight.status === 'booked';
        });
        console.log(`  ✈️ Show ${index} has ${showData.flights.length} flight(s), ${bookedFlights.length} booked`);
        flightsBooked += bookedFlights.length;
      }
    });
    
    // Count hotels: count all hotels and booked ones separately
    tourPlan.shows?.forEach((show, index) => {
      const showData = typeof show === 'object' ? show : null;
      if (showData?.hotels && showData.hotels.length > 0) {
        hotelsTotal += showData.hotels.length;
        const bookedHotels = showData.hotels.filter(hotel => hotel.status === 'booked');
        console.log(`  🏨 Show ${index} has ${showData.hotels.length} hotel(s), ${bookedHotels.length} booked`);
        hotelsBooked += bookedHotels.length;
      }
    });
    
    // Count transport: count all transport and booked ones separately
    tourPlan.shows?.forEach((show, index) => {
      const showData = typeof show === 'object' ? show : null;
      if (showData?.transport && showData.transport.length > 0) {
        transportTotal += showData.transport.length;
        const bookedTransport = showData.transport.filter(t => 
          t.status === 'booked' || t.status === 'confirmed'
        );
        console.log(`  🚗 Show ${index} has ${showData.transport.length} transport(s), ${bookedTransport.length} booked`);
        transportBooked += bookedTransport.length;
      }
    });
    
    console.log(`📊 Progress calculated: ${flightsBooked}/${flightsTotal} flights, ${hotelsBooked}/${hotelsTotal} hotels, ${transportBooked}/${transportTotal} transport`);
    
    return { flightsBooked, hotelsBooked, transportBooked, flightsTotal, hotelsTotal, transportTotal };
  };
  
  const actualProgress = calculateActualProgress();
  const flightsBooked = actualProgress.flightsBooked || 0;
  const hotelsBooked = actualProgress.hotelsBooked || 0;
  const transportBooked = actualProgress.transportBooked || 0;
  
  const flightsTotal = actualProgress.flightsTotal || tourPlan.progress.flightsTotal || 1;
  const hotelsTotal = actualProgress.hotelsTotal || tourPlan.progress.hotelsTotal || 1;
  const transportTotal = actualProgress.transportTotal || tourPlan.progress.transportTotal || 1;
  
  const flightProgress = (flightsBooked / flightsTotal) * 100;
  const hotelProgress = (hotelsBooked / hotelsTotal) * 100;
  const transportProgress = (transportBooked / transportTotal) * 100;
  const overallProgress = ((flightsBooked + hotelsBooked + transportBooked) / 
    (flightsTotal + hotelsTotal + transportTotal)) * 100;

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Tour Overview</span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/tour-manager/view-all')}
                className="text-[#FF9500] text-[12px] font-normal cursor-pointer hover:opacity-70"
              >
                New Plan
              </button>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Back
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-6">
            {/* Tour Name & Status */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                {editingName ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-white text-[14px] focus:outline-none focus:border-[#FF9500]"
                      autoFocus
                      onKeyPress={(e) => e.key === 'Enter' && handleUpdateName()}
                    />
                    <button
                      onClick={handleUpdateName}
                      className="text-[#FF9500] text-[11px] hover:opacity-70"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingName(false);
                        setNewName(tourPlan.name);
                      }}
                      className="text-[rgba(255,255,255,0.5)] text-[11px] hover:opacity-70"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-white text-[14px] font-semibold">{tourPlan.name}</h2>
                    <button
                      onClick={() => setEditingName(true)}
                      className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                      title="Edit tour name"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
                <span className={`text-[10px] px-2 py-1 rounded ${
                  tourPlan.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                  tourPlan.status === 'active' ? 'bg-[rgba(255,149,0,0.2)] text-[#FF9500]' :
                  'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.5)]'
                }`}>
                  {tourPlan.status.toUpperCase()}
                </span>
              </div>
              
              <div className="space-y-2 text-[11px]">
                <div className="flex items-center gap-2 text-[rgba(255,255,255,0.7)]">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Home: {tourPlan.homeCity}</span>
                </div>
                <div className="flex items-center gap-2 text-[rgba(255,255,255,0.7)]">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{tourPlan.totalDays} days • {tourPlan.shows.length} shows</span>
                </div>
              </div>
            </div>

            {/* Shows in Tour */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white text-[12px] font-semibold">Shows in Tour ({tourPlan.shows.length})</span>
                <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                  Tap to view details
                </span>
              </div>
              
              <div className="space-y-2">
                {tourPlan.shows?.map((show, index) => {
                  const showData = typeof show === 'object' ? show : null;
                  const showId = show._id || show;
                  
                  // Count booked items for this show
                  const bookedFlights = showData?.flights?.filter(f => f.status === 'booked').length || 0;
                  const bookedHotels = showData?.hotels?.filter(h => h.status === 'booked').length || 0;
                  const bookedTransport = showData?.transport?.filter(t => t.status === 'booked' || t.status === 'confirmed').length || 0;
                  const totalFlights = showData?.flights?.length || 0;
                  const totalHotels = showData?.hotels?.length || 0;
                  const totalTransport = showData?.transport?.length || 0;
                  
                  return (
                    <div
                      key={showId}
                      onClick={() => navigate(`/tour-manager/show/${showId}`, {
                        state: { activeTab: 'travel' }
                      })}
                      className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 cursor-pointer hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF9500] flex items-center justify-center">
                              <span className="text-black text-[10px] font-bold">{index + 1}</span>
                            </div>
                            <h4 className="text-white text-[11px] truncate" style={{ fontWeight: 300 }}>
                              {showData?.venue?.name || 'Unknown Venue'}
                            </h4>
                          </div>
                          <div className="text-[rgba(255,255,255,0.6)] text-[10px] mb-1">
                            {showData?.venue?.city || 'Unknown City'}{showData?.venue?.state ? `, ${showData.venue.state}` : ''}
                          </div>
                          <div className="text-[rgba(255,255,255,0.5)] text-[9px]">
                            {formatDate(showData?.showDate)} • {showData?.showInfo?.showtimes || 'Evening'}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1">
                          {/* Travel Status */}
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            {totalFlights > 0 && (
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] ${
                                bookedFlights > 0 ? 'bg-green-500/20 text-green-400' : 'bg-[rgba(255,149,0,0.2)] text-[#FF9500]'
                              }`}>
                                <span>Flight</span>
                                <span>{bookedFlights}/{totalFlights}</span>
                              </div>
                            )}
                            {totalHotels > 0 && (
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] ${
                                bookedHotels > 0 ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                <span>Hotel</span>
                                <span>{bookedHotels}/{totalHotels}</span>
                              </div>
                            )}
                            {totalTransport > 0 && (
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] ${
                                bookedTransport > 0 ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                <span>Transport</span>
                                <span>{bookedTransport}/{totalTransport}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Book Travel Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tour-manager/show/${showId}`, {
                                state: { activeTab: 'travel' }
                              });
                            }}
                            className="text-[#FF9500] text-[10px] font-semibold px-2 py-1 rounded hover:bg-[rgba(255,149,0,0.1)] transition-all underline underline-offset-2 hover:no-underline whitespace-nowrap"
                          >
                            Book Travel
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions - Removed: Search Flights, Search Hotels, Add Transport */}
            {/* Travel booking now happens per show via individual show travel tabs */}

            {/* Overall Progress */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-[12px] font-semibold">Overall Progress</span>
                <span className={`text-[12px] font-bold ${getProgressColor(
                  flightsBooked + hotelsBooked + transportBooked,
                  flightsTotal + hotelsTotal + transportTotal
                )}`}>
                  {overallProgress.toFixed(0)}%
                </span>
              </div>
              
              <div className="w-full h-2 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden mb-4">
                <div 
                  className="h-full bg-gradient-to-r from-[#FF9500] to-[#FF7700] transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>

              {/* Flight Progress */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">Flights</span>
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">
                    {flightsBooked}/{flightsTotal}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#FF9500] transition-all duration-300"
                    style={{ width: `${flightProgress}%` }}
                  ></div>
                </div>
              </div>

              {/* Hotel Progress */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">Hotels</span>
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">
                    {hotelsBooked}/{hotelsTotal}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 transition-all duration-300"
                    style={{ width: `${hotelProgress}%` }}
                  ></div>
                </div>
              </div>

              {/* Transport Progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">Transport</span>
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">
                    {transportBooked}/{transportTotal}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${transportProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Timeline with Booking Status */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
              <span className="text-white text-[12px] font-semibold mb-3 block">Timeline</span>
              
              {(() => {
                // Build combined timeline: original timeline items + all booked travel items
                const timelineItems = [];
                const matchedFlightTimes = new Set();
                const matchedHotelTimes = new Set();
                const matchedTransportTimes = new Set();
                
                // First, process original timeline items and match with actual booked items
                tourPlan.timeline?.forEach((item, index) => {
                  let isBooked = false;
                  let flightType = null;
                  let actualTitle = item.title;
                  let actualDescription = item.description;
                  
                  if (item.type === 'flight') {
                    tourPlan.shows?.forEach(show => {
                      const showData = typeof show === 'object' ? show : null;
                      if (showData?.flights) {
                        const matchingFlight = showData.flights.find(flight => {
                          const flightTime = flight.departure?.time || flight.departureTime || flight.departure_time;
                          const timelineTime = item.dateTime;
                          
                          if (!flightTime || !timelineTime) return false;
                          
                          const flightDate = new Date(flightTime);
                          const timelineDate = new Date(timelineTime);
                          const timeDiff = Math.abs(flightDate - timelineDate);
                          const hoursDiff = timeDiff / (1000 * 60 * 60);
                          
                          if (hoursDiff <= 2 && flight.status === 'booked') {
                            const origin = flight.origin || flight.departure?.airport || flight.departureAirport || '';
                            const destination = flight.destination || flight.arrival?.airport || flight.arrivalAirport || '';
                            const airline = flight.airline || flight.airlineName || '';
                            const flightNum = flight.flight_number || flight.flightNumber || '';
                            
                            if (origin && destination) {
                              actualTitle = `Flight: ${origin} → ${destination}`;
                            }
                            
                            const descParts = [];
                            if (airline) descParts.push(airline);
                            if (flightNum) descParts.push(flightNum);
                            if (descParts.length > 0) {
                              actualDescription = descParts.join(' ');
                            }
                            
                            flightType = flight.flightType || flight.flight_type || 
                                        (item.title?.toLowerCase().includes('return') ? 'return' : 
                                         item.title?.toLowerCase().includes('departure') ? 'outbound' : 'connecting');
                            
                            matchedFlightTimes.add(flightTime);
                            return true;
                          }
                          return false;
                        });
                        if (matchingFlight) isBooked = true;
                      }
                    });
                  } else if (item.type === 'hotel') {
                    tourPlan.shows?.forEach(show => {
                      const showData = typeof show === 'object' ? show : null;
                      if (showData?.hotels) {
                        const matchingHotel = showData.hotels.find(hotel => {
                          const checkIn = hotel.checkIn || hotel.check_in_date || hotel.check_in;
                          if (!checkIn || !item.dateTime) return false;
                          
                          const hotelDate = new Date(checkIn);
                          const timelineDate = new Date(item.dateTime);
                          const timeDiff = Math.abs(hotelDate - timelineDate);
                          const hoursDiff = timeDiff / (1000 * 60 * 60);
                          
                          if (hoursDiff <= 24 && hotel.status === 'booked') {
                            const hotelName = hotel.name || '';
                            const hotelAddress = typeof hotel.address === 'string' ? hotel.address : '';
                            const hotelCity = hotel.city || '';
                            const hotelBrand = hotel.brand || '';
                            
                            if (hotelName) {
                              actualTitle = `Hotel Check-in: ${hotelName}`;
                            }
                            
                            const descParts = [];
                            if (hotelAddress) descParts.push(hotelAddress);
                            if (hotelCity) descParts.push(hotelCity);
                            if (hotelBrand) descParts.push(hotelBrand);
                            if (descParts.length > 0) {
                              actualDescription = descParts.join(' ');
                            }
                            
                            matchedHotelTimes.add(checkIn);
                            return true;
                          }
                          return false;
                        });
                        if (matchingHotel) isBooked = true;
                      }
                    });
                  } else if (item.type === 'transport' || item.type === 'ground-transport') {
                    tourPlan.shows?.forEach(show => {
                      const showData = typeof show === 'object' ? show : null;
                      if (showData?.transport) {
                        const matchingTransport = showData.transport.find(transport => {
                          const pickupTime = transport.pickup?.time || transport.pickupTime || transport.pickup_time;
                          if (!pickupTime || !item.dateTime) return false;
                          
                          const transportDate = new Date(pickupTime);
                          const timelineDate = new Date(item.dateTime);
                          const timeDiff = Math.abs(transportDate - timelineDate);
                          const hoursDiff = timeDiff / (1000 * 60 * 60);
                          
                          if (hoursDiff <= 2 && (transport.status === 'booked' || transport.status === 'confirmed')) {
                            const pickupLoc = typeof transport.pickup?.location === 'string' ? transport.pickup.location : '';
                            const vehicleType = typeof transport.vehicleInfo?.type === 'string' ? transport.vehicleInfo.type : 
                                              typeof transport.vehicleType === 'string' ? transport.vehicleType : '';
                            const provider = typeof transport.provider === 'string' ? transport.provider : '';
                            
                            if (pickupLoc) {
                              actualTitle = `Transport: ${pickupLoc}`;
                            }
                            
                            const descParts = [];
                            if (vehicleType) descParts.push(vehicleType);
                            if (provider) descParts.push(provider);
                            if (descParts.length > 0) {
                              actualDescription = descParts.join(' ');
                            }
                            
                            matchedTransportTimes.add(pickupTime);
                            return true;
                          }
                          return false;
                        });
                        if (matchingTransport) isBooked = true;
                      }
                    });
                  }
                  
                  timelineItems.push({
                    ...item,
                    isBooked,
                    flightType,
                    actualTitle: actualTitle || item.title,
                    actualDescription: actualDescription || item.description,
                    isFromTimeline: true
                  });
                });
                
                // Then, add all booked flights/hotels/transport that aren't in the timeline
                tourPlan.shows?.forEach((show, showIndex) => {
                  const showData = typeof show === 'object' ? show : null;
                  
                  // Add all booked flights
                  if (showData?.flights) {
                    showData.flights.forEach(flight => {
                      const departureTime = flight.departure?.time || flight.departureTime || flight.departure_time;
                      const arrivalTime = flight.arrival?.time || flight.arrivalTime || flight.arrival_time;
                      const flightType = flight.flightType || flight.flight_type || 'connecting';
                      
                      if (departureTime && flight.status === 'booked' && !matchedFlightTimes.has(departureTime)) {
                        const origin = flight.origin || flight.departure?.airport || flight.departureAirport || '';
                        const destination = flight.destination || flight.arrival?.airport || flight.arrivalAirport || '';
                        const airline = flight.airline || flight.airlineName || '';
                        const flightNum = flight.flight_number || flight.flightNumber || '';
                        
                        timelineItems.push({
                          type: 'flight',
                          dateTime: departureTime,
                          actualTitle: `Flight: ${origin || 'N/A'} → ${destination || 'N/A'}`,
                          actualDescription: [airline, flightNum].filter(Boolean).join(' ') || 'Flight details',
                          isBooked: true,
                          flightType,
                          isFromTimeline: false
                        });
                      }
                      
                      if (arrivalTime && arrivalTime !== departureTime && flight.status === 'booked') {
                        const destination = flight.destination || flight.arrival?.airport || flight.arrivalAirport || '';
                        const airline = flight.airline || flight.airlineName || '';
                        const flightNum = flight.flight_number || flight.flightNumber || '';
                        
                        timelineItems.push({
                          type: 'flight-arrival',
                          dateTime: arrivalTime,
                          actualTitle: `Arrival: ${destination || 'N/A'}`,
                          actualDescription: [airline, flightNum].filter(Boolean).join(' ') || 'Flight arrival',
                          isBooked: true,
                          flightType,
                          isFromTimeline: false
                        });
                      }
                    });
                  }
                  
                  // Add all booked hotels
                  if (showData?.hotels) {
                    showData.hotels.forEach(hotel => {
                      const checkIn = hotel.checkIn || hotel.check_in_date || hotel.check_in;
                      const checkOut = hotel.checkOut || hotel.check_out_date || hotel.check_out;
                      
                      if (checkIn && hotel.status === 'booked' && !matchedHotelTimes.has(checkIn)) {
                        const hotelName = hotel.name || '';
                        const hotelAddress = typeof hotel.address === 'string' ? hotel.address : '';
                        const hotelCity = hotel.city || '';
                        const hotelBrand = hotel.brand || '';
                        
                        timelineItems.push({
                          type: 'hotel',
                          dateTime: checkIn,
                          actualTitle: `Hotel Check-in: ${hotelName || 'Hotel'}`,
                          actualDescription: [hotelAddress, hotelCity, hotelBrand].filter(Boolean).join(' ') || 'Hotel details',
                          isBooked: true,
                          isFromTimeline: false
                        });
                      }
                      
                      if (checkOut && checkOut !== checkIn && hotel.status === 'booked') {
                        const hotelName = hotel.name || '';
                        const hotelAddress = typeof hotel.address === 'string' ? hotel.address : '';
                        const hotelCity = hotel.city || '';
                        
                        timelineItems.push({
                          type: 'hotel-checkout',
                          dateTime: checkOut,
                          actualTitle: `Hotel Check-out: ${hotelName || 'Hotel'}`,
                          actualDescription: [hotelAddress, hotelCity].filter(Boolean).join(' ') || 'Hotel checkout',
                          isBooked: true,
                          isFromTimeline: false
                        });
                      }
                    });
                  }
                  
                  // Add all booked transport
                  if (showData?.transport) {
                    showData.transport.forEach(transport => {
                      const pickupTime = transport.pickup?.time || transport.pickupTime || transport.pickup_time;
                      const dropoffTime = transport.dropoff?.time || transport.dropoffTime || transport.dropoff_time;
                      
                      if (pickupTime && (transport.status === 'booked' || transport.status === 'confirmed') && !matchedTransportTimes.has(pickupTime)) {
                        const pickupLoc = typeof transport.pickup?.location === 'string' ? transport.pickup.location : '';
                        const vehicleType = typeof transport.vehicleInfo?.type === 'string' ? transport.vehicleInfo.type : 
                                          typeof transport.vehicleType === 'string' ? transport.vehicleType : '';
                        const provider = typeof transport.provider === 'string' ? transport.provider : '';
                        
                        timelineItems.push({
                          type: 'transport',
                          dateTime: pickupTime,
                          actualTitle: `Transport: ${pickupLoc || 'Pickup'}`,
                          actualDescription: [vehicleType, provider].filter(Boolean).join(' ') || 'Transport details',
                          isBooked: true,
                          isFromTimeline: false
                        });
                      }
                      
                      if (dropoffTime && dropoffTime !== pickupTime && (transport.status === 'booked' || transport.status === 'confirmed')) {
                        const dropoffLoc = typeof transport.dropoff?.location === 'string' ? transport.dropoff.location : '';
                        const vehicleType = typeof transport.vehicleInfo?.type === 'string' ? transport.vehicleInfo.type : 
                                          typeof transport.vehicleType === 'string' ? transport.vehicleType : '';
                        const provider = typeof transport.provider === 'string' ? transport.provider : '';
                        
                        timelineItems.push({
                          type: 'transport-dropoff',
                          dateTime: dropoffTime,
                          actualTitle: `Transport: ${dropoffLoc || 'Dropoff'}`,
                          actualDescription: [vehicleType, provider].filter(Boolean).join(' ') || 'Transport dropoff',
                          isBooked: true,
                          isFromTimeline: false
                        });
                      }
                    });
                  }
                });
                
                // Sort all items by date/time
                timelineItems.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
                
                if (timelineItems.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                        No travel items booked yet
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {timelineItems.map((item, index) => (
                      <div key={index} className="relative pl-6">
                        {/* Timeline line */}
                        {index < timelineItems.length - 1 && (
                          <div className="absolute left-2 top-5 w-px h-full bg-[rgba(255,255,255,0.1)]"></div>
                        )}

                        {/* Icon */}
                        <div className={`absolute left-0 top-0 w-4 h-4 rounded-full flex items-center justify-center ${
                          item.isBooked ? 'bg-green-500' :
                          item.type === 'flight' || item.type === 'flight-arrival' ? 'bg-[#FF9500]' :
                          item.type === 'hotel' || item.type === 'hotel-checkout' ? 'bg-purple-500' :
                          item.type === 'transport' || item.type === 'transport-dropoff' || item.type === 'ground-transport' ? 'bg-blue-500' :
                          'bg-[rgba(34,197,94,0.8)]'
                        }`}>
                          {item.isBooked && (
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {!item.isBooked && (
                            <span className="text-[8px]">
                              {item.type === 'flight' || item.type === 'flight-arrival' ? 'F' : 
                               item.type === 'hotel' || item.type === 'hotel-checkout' ? 'H' : 
                               item.type === 'transport' || item.type === 'transport-dropoff' || item.type === 'ground-transport' ? 'T' : 'S'}
                            </span>
                          )}
                        </div>

                        {/* Content */}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {formatDateTime(item.dateTime)}
                            </p>
                            {item.isBooked && (
                              <span className="text-[8px] px-1.5 py-0.5 bg-green-500/20 text-green-500 rounded">
                                BOOKED
                              </span>
                            )}
                            {item.type === 'flight' && item.flightType && (
                              <span className="text-[8px] px-1.5 py-0.5 bg-[rgba(255,149,0,0.2)] text-[#FF9500] rounded">
                                {item.flightType === 'return' ? 'RETURN' : 
                                 item.flightType === 'outbound' || item.flightType === 'departure' ? 'DEPARTURE' : 
                                 'CONNECTING'}
                              </span>
                            )}
                          </div>
                          <p className="text-white text-[11px] font-medium">{item.actualTitle || item.title}</p>
                          {item.actualDescription && typeof item.actualDescription === 'string' && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-0.5">{item.actualDescription}</p>
                          )}
                          {!item.actualDescription && item.description && typeof item.description === 'string' && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-0.5">{item.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Warnings */}
            {tourPlan.warnings && tourPlan.warnings.length > 0 && (
              <div className="mt-4 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-[#FF9500] text-[11px] font-semibold mb-1">Alerts</p>
                    <ul className="text-[rgba(255,255,255,0.7)] text-[10px] space-y-1">
                      {tourPlan.warnings.map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TourOverview;

